import { ManageAccessWindow } from '../../components';
import { Section, FeatureRows, Note, MoreLink } from '../../components/page';
import UseCaseLayout from './UseCaseLayout';
import type { UseCaseData } from './UseCaseLayout';

const DEPLOYMENT = [
  {
    title: 'Self-hosted on Enterprise',
    body: 'On Enterprise, Statio ships as licensed containers that run in your own cloud. Free through Business run on Statio’s managed cloud.',
  },
  {
    title: 'SSO and SAML',
    body: 'From Business up, members sign in through your identity provider with SSO/SAML, and you can require it for everyone on your domain, alongside full role-based access control.',
  },
  {
    title: 'Logs to your SIEM',
    body: (
      <>
        Webhooks deliver tool calls, audit events or both to your security tooling, signed with HMAC in the{' '}
        <code>X-Statio-Signature</code> header so you can verify they came from Statio. SIEM export is on Business and
        up.
      </>
    ),
  },
];

const data: UseCaseData = {
  title: 'For enterprise security teams',
  subtitle:
    'Decide what your AI agents may call. Access is granted one tool at a time, every call is recorded, and any agent can be cut off on its own.',
  art: 'portico',
  problemTitle: '50 agents calling APIs with zero governance.',
  problemText:
    'Your company has dozens of AI agents calling APIs with no centralized control. No one knows which agents have which keys, there is no audit trail, and you cannot revoke access to a single agent without breaking everything. One compromised agent means rotating every key manually.',
  solutionTitle: 'Statio is the checkpoint.',
  solutionText:
    "Statio is where your agents' API access is decided. You grant tools one at a time rather than whole servers, every call is recorded, and revoking one agent leaves the rest running.",
  benefits: [
    {
      title: 'Access decided per tool, not per server',
      body: 'Grant a member or an agent a server, then deny the tools it should not call. A tool-level deny overrides the server grant, and the gateway checks it on every call before a credential is used.',
    },
    {
      title: 'Every agent has its own identity',
      body: 'Agents are principals with their own token, status and optional expiry, so the log names the agent, not the engineer who set it up.',
    },
    {
      title: 'Per-customer credential isolation, with no cross-contamination',
      body: 'Each customer’s credentials are isolated, and tenant boundaries are enforced at the gateway layer.',
    },
    {
      title: 'Short-lived tokens with automatic refresh',
      body: 'Access tokens and agent tokens last five minutes. People’s sessions refresh for up to seven days.',
    },
    {
      title: 'A record of every tool call: identity, timestamp, latency, outcome',
      body: 'Activity shows each call as allowed or failed, with the caller, tool, server and status code. A refused call is a 403 with the reason.',
    },
    {
      title: 'Revoke one agent without touching the rest',
      body: 'Suspend or revoke an agent from the dashboard and it is refused within seconds, with nothing to redeploy. Every other agent keeps running.',
    },
    {
      title: 'Audit logs, exportable for compliance review',
      body: 'Admin actions are recorded as plain sentences: who granted what, to whom, on which server, and the result. Business and Enterprise can export them as CSV or NDJSON, along with every tool call and model call.',
    },
  ],
  screen: {
    title: 'What a per-tool deny looks like',
    lede: 'The Manage Access drawer for one agent on one server. The server grant covers every tool; the one denied tool is refused at the gateway. Illustrative data.',
    window: <ManageAccessWindow />,
  },
  extra: (
    <>
      <Section id="deployment" title="Where it runs and how it plugs in" split>
        <FeatureRows items={DEPLOYMENT} columns={1} />
      </Section>
      <Section id="compliance" title="Compliance" split>
        <Note title="No completed certification yet">
          Statio has no completed compliance certification, and we will not imply one. Audit logs are exportable for
          your review today. If a certification is a hard requirement, tell us and we will tell you exactly where we
          are. <MoreLink href="/security">Read the security page</MoreLink>
        </Note>
      </Section>
    </>
  ),
  otherCases: [
    {
      label: 'For AI agent developers',
      slug: 'developers',
      body: 'Replace the API keys in every agent config with one Statio key, and give each agent its own identity.',
    },
    {
      label: 'For API providers',
      slug: 'providers',
      body: 'Publish your API to agents without building MCP hosting, auth and rate limiting yourself.',
    },
  ],
};

export default function EnterprisePage() {
  return <UseCaseLayout data={data} />;
}
